import { apiClient } from '../api/client'

/**
 * Dosya ekinin **aynı ekranda** (pop-up içinde) önizlenmesi — 108.
 *
 * <p><b>Neden backend'e "inline" bir uç eklenmedi:</b> içerik zaten kimlik-doğrulamalı olarak blob
 * halinde çekiliyor (`downloadAttachment` ile aynı uç, aynı yetki/kapsam kontrolü). Blob elde
 * olduğunda tarayıcıda göstermek tümüyle istemci işidir; ayrı bir `Content-Disposition: inline` ucu
 * hem ikinci bir yetki yüzeyi hem ikinci bir bakım noktası olurdu.
 */

/** Tarayıcıda gömülü gösterilebilen uzantılar. Diğerlerinde önizleme yerine indirme sunulur. */
const PREVIEWABLE: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
}

/** Önizlemenin nasıl çizileceği — çağıran buna göre `<iframe>` ya da `<img>` seçer. */
export type PreviewKind = 'pdf' | 'image'

/**
 * Uzantı önizlenebilir mi? Depo yalnız `doc/docx/xls/xlsx/ppt/pptx/pdf/png/jpg/jpeg/zip/rar` kabul
 * ediyor (`StorageValidator`); bunlardan tarayıcıda gömülü açılabilenler PDF ve görsellerdir. Office
 * ve arşiv dosyaları için önizleme **sunulmaz** — "açılmayan bir düğme" göstermektense indirme kalır.
 */
export function previewKindOf(extension: string | null | undefined): PreviewKind | null {
  const ext = (extension ?? '').toLowerCase()
  if (!PREVIEWABLE[ext]) return null
  return ext === 'pdf' ? 'pdf' : 'image'
}

/**
 * Eki çeker ve önizleme için bir object URL üretir.
 *
 * <p><b>MIME sunucudan gelen değerle DEĞİL, uzantıdan seçilir</b> ve blob yeniden paketlenir. Sebep
 * güvenlik: object URL'i oluşturan belgenin kaynağını (origin) devralır, dolayısıyla yanlış/kötü
 * niyetli bir MIME ile gelen içerik `<iframe>` içinde **bizim origin'imizde** çalıştırılabilirdi.
 * Tipi burada sabitlemek, içeriğin yalnız PDF görüntüleyici ya da görsel olarak yorumlanmasını
 * garantiler (yükleme doğrulayıcısı HTML/SVG'ye zaten izin vermiyor — bu ikinci savunma katmanı).
 *
 * <p>Dönen URL çağıran tarafından **serbest bırakılmalıdır** (`URL.revokeObjectURL`); aksi hâlde
 * sekme kapanana kadar bellekte kalır.
 */
export async function fetchAttachmentPreviewUrl(id: number, extension: string): Promise<string> {
  const { data } = await apiClient.get<Blob>(`/management/attachments/${id}`, {
    responseType: 'blob',
  })
  const safeType = PREVIEWABLE[extension.toLowerCase()] ?? 'application/octet-stream'
  return URL.createObjectURL(new Blob([data], { type: safeType }))
}
